import { stripSplitBlockId } from './pagination/utils';
import type { ReportData } from './types';
import type { TableBlock, TestBlock } from '@/types/report-payload';

export type ReportFootnote = {
    id: string;
    number: number;
    text: string;
    blockId: string;
};

export type ReportFootnotePlan = {
    all: ReportFootnote[];
    byBlockId: Record<string, ReportFootnote[]>;
};

export const EMPTY_REPORT_FOOTNOTE_PLAN: ReportFootnotePlan = {
    all: [],
    byBlockId: {},
};

export const REPORT_FOOTNOTE_OVERHEAD_MEASUREMENT_ID = 'report-footnotes-overhead';

const FOOTNOTE_ENTRY_GAP = 4;
const DEFAULT_FOOTNOTE_ENTRY_HEIGHT = 18;
const DEFAULT_FOOTNOTE_OVERHEAD_HEIGHT = 12;

export const getFootnoteMeasurementId = (footnoteId: string) => `footnote-${footnoteId}`;

export const getFootnoteBlockId = (block: Pick<TestBlock, 'id' | 'order'>) =>
    stripSplitBlockId(block.id || block.order);

const isSatepsiReference = (reference: { source_provider?: string }) =>
    reference.source_provider?.toLowerCase() === 'satepsi';

const getTableBlocks = (data: ReportData): TableBlock[] => {
    return (data.tests?.blocks || []).filter(
        (block): block is TableBlock => block.block_type === 'table',
    );
};

export const getSatepsiReferenceIds = (data: ReportData): string[] => {
    const ids = new Set<string>();

    for (const block of getTableBlocks(data)) {
        for (const reference of block.references || []) {
            if (!reference.source_test_id || !isSatepsiReference(reference)) {
                continue;
            }

            ids.add(reference.source_test_id);
        }
    }

    return Array.from(ids);
};

/**
 * `referencesById` is only the Satepsi half — a free instrument carries its own
 * citation on the block, so a report with no catalog instruments can still have
 * footnotes and the map is legitimately undefined here.
 */
export const buildReportFootnotePlan = (
    data: ReportData,
    referencesById: Record<string, string> | undefined,
): ReportFootnotePlan => {
    const all: ReportFootnote[] = [];
    const byBlockId: Record<string, ReportFootnote[]> = {};

    for (const block of getTableBlocks(data)) {
        const blockId = getFootnoteBlockId(block);
        const blockFootnotes: ReportFootnote[] = [];
        const seenReferenceTexts = new Set<string>();

        // Numbering is global and in document order; dedupe is per block, so
        // two instruments citing the same manual still get a number each.
        const pushFootnote = (rawText: string, key: string) => {
            const text = rawText.trim();
            if (!text || seenReferenceTexts.has(text)) return;

            seenReferenceTexts.add(text);

            const number = all.length + 1;
            const footnote: ReportFootnote = {
                id: `${blockId}-${key}-${number}`,
                number,
                text,
                blockId,
            };

            all.push(footnote);
            blockFootnotes.push(footnote);
        };

        // A free instrument has no catalog id to resolve a citation through,
        // so it stores the text itself.
        if (block.instrument_reference) {
            pushFootnote(block.instrument_reference, 'free');
        }

        for (const reference of block.references || []) {
            if (!reference.source_test_id || !isSatepsiReference(reference)) {
                continue;
            }

            const text = referencesById?.[reference.source_test_id];
            if (!text) continue;

            pushFootnote(text, reference.source_test_id);
        }

        if (blockFootnotes.length > 0) {
            byBlockId[blockId] = blockFootnotes;
        }
    }

    if (all.length === 0) return EMPTY_REPORT_FOOTNOTE_PLAN;

    return { all, byBlockId };
};

export const getFootnoteAdditionalHeight = (
    footnotes: ReportFootnote[],
    measurements: Record<string, number>,
    hasExistingPageFootnotes: boolean,
) => {
    if (footnotes.length === 0) return 0;

    const overhead = hasExistingPageFootnotes
        ? 0
        : (measurements[REPORT_FOOTNOTE_OVERHEAD_MEASUREMENT_ID] ??
          DEFAULT_FOOTNOTE_OVERHEAD_HEIGHT);
    const entriesHeight = footnotes.reduce((total, footnote) => {
        return (
            total +
            (measurements[getFootnoteMeasurementId(footnote.id)] ?? DEFAULT_FOOTNOTE_ENTRY_HEIGHT)
        );
    }, 0);
    const gapCount = hasExistingPageFootnotes
        ? footnotes.length
        : Math.max(0, footnotes.length - 1);

    return overhead + entriesHeight + gapCount * FOOTNOTE_ENTRY_GAP;
};
